import { useReducer, useMemo, useCallback } from 'react';
import DataState from '../../../Types/DataState';
import { SortDirection } from '../../../Types/SortDirection';
import {
  Ownership,
  PastContactsTab,
  PastContactsTableSortColumn,
} from '../PastContactsTypes';
import { prepareGetContactsDataQuery } from '../../../api/PastContacts';
import {
  PastContact,
  SavedContactAction,
} from '../../../api/PastContacts/schema';
import { filterContacts, sortContacts } from '../utils/PastContactsUtils';
import {
  TimeOption,
  TRAILING_DAYS,
} from '../../../PerformancePortal/Dashboard/DashboardComponents/LighthouseMetrics/types';
import { weeksList } from '../../../Util/Week';
import moment from 'moment';
import { useMutation, useQuery } from '@apollo/client';
import {
  GetContactsQuery,
  UpdateSavedContactsMutation,
} from '../../../api/PastContacts/queries';

interface PastContactsState {
  // UI state
  currentTab: PastContactsTab;
  searchInput: string | undefined;
  appliedSearchTerm: string | undefined;
  showFilterSidebar: boolean;

  // Filter state
  selectedChannel: string;
  selectedTimeOfDay: number;
  selectedDuration: number;
  selectedOwnership: Ownership;

  // Time filter state
  timeOption: TimeOption;
  timestamp: string;
  weekValue: string;

  // Sort state
  sortColumn: PastContactsTableSortColumn;
  sortDirection: SortDirection;

  // Pagination state
  currentPage: number;
  itemPerPage: number;
}

type PastContactsAction =
  | { type: 'CHANGE_TAB'; payload: PastContactsTab }
  | { type: 'SET_SEARCH_INPUT'; payload: string | undefined }
  | { type: 'APPLY_SEARCH'; payload: string | undefined }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SELECTED_CHANNEL'; payload: string }
  | { type: 'SET_SELECTED_TIME_OF_DAY'; payload: number }
  | { type: 'SET_SELECTED_DURATION'; payload: number }
  | { type: 'SET_SELECTED_OWNERSHIP'; payload: Ownership }
  | { type: 'RESET_FILTERS' }
  | {
      type: 'SET_TIME_FILTER';
      payload: { timeOption: TimeOption; timestamp: string };
    }
  | { type: 'SET_WEEK_VALUE'; payload: string }
  | {
      type: 'SET_SORT';
      payload: {
        column: PastContactsTableSortColumn;
        direction: SortDirection;
      };
    }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'SET_ITEMS_PER_PAGE'; payload: number };

const defaultItemsPerPage = 25;

// Column width configurations
const defaultColumnWidths = {
  contactId: '200px',
  dateTime: '200px',
  channel: '100px',
  duration: '100px',
  sic: '250px',
  ownership: '160px',
  review: '100px',
  bookmarked: '100px',
};

const filteredColumnWidths = {
  contactId: '160px',
  dateTime: '160px',
  channel: '100px',
  duration: '100px',
  sic: '0px',
  ownership: '160px',
  review: '80px',
  bookmarked: '80px',
};

const initialState: PastContactsState = {
  // UI state
  currentTab: PastContactsTab.ALL,
  searchInput: undefined,
  appliedSearchTerm: undefined,
  showFilterSidebar: false,

  // Filter state
  selectedChannel: 'All',
  selectedTimeOfDay: 0,
  selectedDuration: 0,
  selectedOwnership: Ownership.ALL,

  // Time filter state - default to trailing 7 days (DAILY)
  timeOption: TimeOption.DAILY,
  timestamp: moment().format('YYYY-MM-DD'),
  weekValue: TRAILING_DAYS,

  // Sort state
  sortColumn: PastContactsTableSortColumn.TIME,
  sortDirection: SortDirection.DESCENDING,

  // Pagination state
  currentPage: 1,
  itemPerPage: defaultItemsPerPage,
};

const pastContactsReducer = (
  state: PastContactsState,
  action: PastContactsAction
): PastContactsState => {
  switch (action.type) {
    case 'CHANGE_TAB':
      return {
        ...state,
        currentTab: action.payload,
        // Reset pagination when tab changes
        currentPage: 1,
        itemPerPage: defaultItemsPerPage,
      };

    case 'SET_SEARCH_INPUT':
      return {
        ...state,
        searchInput: action.payload,
      };

    case 'APPLY_SEARCH':
      return {
        ...state,
        appliedSearchTerm: action.payload,
        currentPage: 1, // Reset to first page when searching
      };

    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        showFilterSidebar: !state.showFilterSidebar,
      };

    case 'SET_SELECTED_CHANNEL':
      return {
        ...state,
        selectedChannel: action.payload,
        currentPage: 1, // Reset to first page when filtering
      };

    case 'SET_SELECTED_TIME_OF_DAY':
      return {
        ...state,
        selectedTimeOfDay: action.payload,
        currentPage: 1, // Reset to first page when filtering
      };

    case 'SET_SELECTED_DURATION':
      return {
        ...state,
        selectedDuration: action.payload,
        currentPage: 1, // Reset to first page when filtering
      };

    case 'SET_SELECTED_OWNERSHIP':
      return {
        ...state,
        selectedOwnership: action.payload,
        currentPage: 1, // Reset to first page when filtering
      };

    case 'RESET_FILTERS':
      return {
        ...state,
        selectedChannel: 'All',
        selectedTimeOfDay: 0,
        selectedDuration: 0,
        selectedOwnership: Ownership.ALL,
        currentPage: 1, // Reset to first page when resetting filters
      };

    case 'SET_TIME_FILTER':
      return {
        ...state,
        timeOption: action.payload.timeOption,
        timestamp: action.payload.timestamp,
        currentPage: 1, // Reset to first page when changing time filter
      };

    case 'SET_WEEK_VALUE':
      return {
        ...state,
        weekValue: action.payload,
        currentPage: 1, // Reset to first page when changing week filter
      };

    case 'SET_SORT':
      return {
        ...state,
        sortColumn: action.payload.column,
        sortDirection: action.payload.direction,
      };

    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };

    case 'SET_ITEMS_PER_PAGE':
      return {
        ...state,
        itemPerPage: action.payload,
      };

    default:
      return state;
  }
};

export interface UsePaginatedPastContactsResult {
  // Current state
  currentTab: PastContactsTab;
  searchInput: string | undefined;
  showFilterSidebar: boolean;
  contactsState: string;
  loading: boolean;

  // Filter state
  selectedChannel: string;
  selectedTimeOfDay: number;
  selectedDuration: number;
  selectedOwnership: Ownership;

  // Time filter state
  timeOption: TimeOption;
  timestamp: string;

  // Week filter state
  weekValue: string;
  weekOptions: string[];

  // Sort state
  sortColumn: PastContactsTableSortColumn;
  sortDirection: SortDirection;

  // Pagination state
  currentPage: number;
  itemPerPage: number;

  // Column configuration
  columnWidths: typeof defaultColumnWidths;
  showSicColumn: boolean;

  // Computed data (the 3 memos - lift and drop)
  paginatedData: PastContact[];
  paginationMeta: {
    numberOfPages: number;
    firstVisibleIndex: number;
    lastVisibleIndex: number;
    visibleRecordsLen: number;
  };

  // Event handlers (using useCallback for performance)
  handleTabChange: (tab: PastContactsTab) => void;
  handleSearchSubmit: () => void;
  handleToggleSidebars: () => void;
  handleSort: (
    column: PastContactsTableSortColumn,
    direction: SortDirection
  ) => void;
  setSearchInput: (value: string | undefined) => void;
  setCurrentPage: (page: number) => void;
  setItemPerPage: (count: number) => void;
  handleBookmarkClick: (pastContact: PastContact) => void;
  setSelectedChannel: (channel: string) => void;
  setSelectedTimeOfDay: (timeOfDay: number) => void;
  setSelectedDuration: (duration: number) => void;
  setSelectedOwnership: (ownership: Ownership) => void;
  resetFilters: () => void;
  setTimeFilter: (timeOption: TimeOption, timestamp: string) => void;
  handleWeekChange: (selectedWeek: string) => void;
}

export const usePaginatedPastContacts = (
  requesterUserId: string,
  cscEntityPrefix: string
): UsePaginatedPastContactsResult => {
  const [state, dispatch] = useReducer(pastContactsReducer, initialState);

  const queryVariables = prepareGetContactsDataQuery(
    requesterUserId,
    cscEntityPrefix,
    state.timeOption,
    state.timestamp
  );

  const { loading, error, data } = useQuery(GetContactsQuery, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network', // For real-time updates
  });

  // Apollo mutation for optimistic bookmark updates
  const [updateSavedContactMutation] = useMutation(
    UpdateSavedContactsMutation,
    {
      optimisticResponse: variables => ({
        updateSavedContacts: {
          status: true,
          __typename: 'UpdateSavedContactResponse',
        },
      }),

      update: (cache, { data }, { variables }) => {
        try {
          const existingData = cache.readQuery({
            query: GetContactsQuery,
            variables: queryVariables,
          });

          if (existingData?.getContacts && variables) {
            const isAdding = variables.action === SavedContactAction.ADD;
            const contactId = variables.contactId;

            if (isAdding) {
              // Adding: Update pastContacts + add to savedContacts
              const updatedPastContacts =
                existingData.getContacts.pastContacts.map(contact =>
                  contact.COMM_ID === contactId
                    ? { ...contact, SAVED: true }
                    : contact
                );

              const updatedContact = updatedPastContacts.find(
                contact => contact.COMM_ID === contactId
              );
              const updatedSavedContacts = [
                ...existingData.getContacts.savedContacts,
                updatedContact,
              ];

              cache.writeQuery({
                query: GetContactsQuery,
                variables: queryVariables,
                data: {
                  ...existingData,
                  getContacts: {
                    ...existingData.getContacts,
                    pastContacts: updatedPastContacts,
                    savedContacts: updatedSavedContacts,
                  },
                },
              });
            } else {
              // Removing: Remove from savedContacts + update pastContacts if exists
              const updatedSavedContacts =
                existingData.getContacts.savedContacts.filter(
                  contact => contact.COMM_ID !== contactId
                );

              const updatedPastContacts =
                existingData.getContacts.pastContacts.map(contact =>
                  contact.COMM_ID === contactId
                    ? { ...contact, SAVED: false }
                    : contact
                );

              cache.writeQuery({
                query: GetContactsQuery,
                variables: queryVariables,
                data: {
                  ...existingData,
                  getContacts: {
                    ...existingData.getContacts,
                    pastContacts: updatedPastContacts,
                    savedContacts: updatedSavedContacts,
                  },
                },
              });
            }
          }
        } catch (error) {
          console.error('Error updating Apollo cache:', error);
        }
      },

      onError: error => {
        // Apollo automatically reverts optimistic updates on error
        console.error('Bookmark update failed:', error);
        // Optional: Show user-friendly error message here
      },
    }
  );

  // Level 1: Current data source (changes with tab) - Filter and Sort
  const currentData = useMemo(() => {
    const apolloData = data?.getContacts ?? {
      pastContacts: [],
      savedContacts: [],
    };
    let preFilteredData =
      state.currentTab === PastContactsTab.ALL
        ? apolloData.pastContacts
        : apolloData.savedContacts;

    let filteredData = filterContacts(
      preFilteredData,
      state.appliedSearchTerm,
      state.selectedChannel,
      state.selectedTimeOfDay,
      state.selectedDuration,
      state.selectedOwnership
    );

    return sortContacts(filteredData, state.sortColumn, state.sortDirection);
  }, [
    data,
    state.currentTab,
    state.appliedSearchTerm,
    state.selectedChannel,
    state.selectedTimeOfDay,
    state.selectedDuration,
    state.selectedOwnership,
    state.sortColumn,
    state.sortDirection,
  ]);

  // Level 2: Basic pagination metadata (cheap calculations)
  const paginationMeta = useMemo(() => {
    const totalRecords = currentData.length;
    const numberOfPages = Math.ceil(totalRecords / state.itemPerPage);
    const firstVisibleIndex = (state.currentPage - 1) * state.itemPerPage;
    const lastVisibleIndex = firstVisibleIndex + state.itemPerPage;
    return {
      numberOfPages,
      firstVisibleIndex,
      lastVisibleIndex,
      visibleRecordsLen: totalRecords,
    };
  }, [currentData.length, state.itemPerPage, state.currentPage]);

  // Level 3: Actual data slicing (most expensive operation)
  const paginatedData = useMemo(() => {
    return currentData.slice(
      paginationMeta.firstVisibleIndex,
      paginationMeta.lastVisibleIndex
    );
  }, [
    currentData,
    paginationMeta.firstVisibleIndex,
    paginationMeta.lastVisibleIndex,
  ]);

  // Event handlers with useCallback for performance optimization
  const handleTabChange = useCallback((newTab: PastContactsTab) => {
    dispatch({ type: 'CHANGE_TAB', payload: newTab });
  }, []);

  const handleSearchSubmit = useCallback(() => {
    // Apply the current input as the search term
    const trimmedSearch = state.searchInput?.trim();
    dispatch({ type: 'APPLY_SEARCH', payload: trimmedSearch || undefined });
  }, [state.searchInput]);

  const handleToggleSidebars = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  }, []);

  const handleSort = useCallback(
    (column: PastContactsTableSortColumn, direction: SortDirection) => {
      dispatch({ type: 'SET_SORT', payload: { column, direction } });
    },
    []
  );

  const setSearchInput = useCallback((value: string | undefined) => {
    dispatch({ type: 'SET_SEARCH_INPUT', payload: value });
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page });
  }, []);

  const setItemPerPage = useCallback((count: number) => {
    dispatch({ type: 'SET_ITEMS_PER_PAGE', payload: count });
  }, []);

  const handleBookmarkClick = useCallback(
    async (pastContact: PastContact) => {
      try {
        await updateSavedContactMutation({
          variables: {
            userId: `${cscEntityPrefix}:${requesterUserId}`,
            contactId: pastContact.COMM_ID,
            dateAndTime: pastContact.TIME,
            channel: pastContact.CHANNEL_NAME,
            duration: pastContact.CALL_DURATION_SECONDS?.toString() ?? '0',
            sic: pastContact.SIC_NODE_1_NAME,
            ownershipType: pastContact.OWNERSHIP_TYPE.toString(),
            action: pastContact.SAVED
              ? SavedContactAction.REMOVE
              : SavedContactAction.ADD,
          },
        });
      } catch (error) {
        // Error already handled by onError callback in useMutation
        // Apollo automatically reverts optimistic updates
        console.error('Bookmark operation failed:', error);
      }
    },
    [updateSavedContactMutation, requesterUserId, cscEntityPrefix]
  );

  const setSelectedChannel = useCallback((channel: string) => {
    dispatch({ type: 'SET_SELECTED_CHANNEL', payload: channel });
  }, []);

  const setSelectedTimeOfDay = useCallback((timeOfDay: number) => {
    dispatch({ type: 'SET_SELECTED_TIME_OF_DAY', payload: timeOfDay });
  }, []);

  const setSelectedDuration = useCallback((duration: number) => {
    dispatch({ type: 'SET_SELECTED_DURATION', payload: duration });
  }, []);

  const setSelectedOwnership = useCallback((ownership: Ownership) => {
    dispatch({ type: 'SET_SELECTED_OWNERSHIP', payload: ownership });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: 'RESET_FILTERS' });
  }, []);

  const setTimeFilter = useCallback(
    (timeOption: TimeOption, timestamp: string) => {
      dispatch({ type: 'SET_TIME_FILTER', payload: { timeOption, timestamp } });
    },
    []
  );

  // Week functionality
  const weekOptions = useMemo(() => [TRAILING_DAYS, ...weeksList()], []);

  const handleWeekChange = useCallback(
    (selectedWeek: string) => {
      // Update the week value
      dispatch({ type: 'SET_WEEK_VALUE', payload: selectedWeek });

      // Update the time filter based on the selected week
      if (selectedWeek === TRAILING_DAYS) {
        // For trailing 7 days, use DAILY with current date
        setTimeFilter(TimeOption.DAILY, moment().format('YYYY-MM-DD'));
      } else {
        // For specific Amazon weeks, use WEEKLY with the week format
        setTimeFilter(TimeOption.WEEKLY, selectedWeek);
      }
    },
    [setTimeFilter]
  );

  // Dynamic column widths based on filter sidebar state
  const columnWidths = useMemo(() => {
    return state.showFilterSidebar ? filteredColumnWidths : defaultColumnWidths;
  }, [state.showFilterSidebar]);

  // Derive loading state from Apollo
  const contactsState = loading
    ? DataState.LOADING
    : error
      ? DataState.ERROR
      : DataState.LOADED;

  return {
    // Current state
    currentTab: state.currentTab,
    searchInput: state.searchInput,
    showFilterSidebar: state.showFilterSidebar,
    contactsState,
    loading,

    // Filter state
    selectedChannel: state.selectedChannel,
    selectedTimeOfDay: state.selectedTimeOfDay,
    selectedDuration: state.selectedDuration,
    selectedOwnership: state.selectedOwnership,

    // Time filter state
    timeOption: state.timeOption,
    timestamp: state.timestamp,

    // Week filter state
    weekValue: state.weekValue,
    weekOptions,

    // Sort state
    sortColumn: state.sortColumn,
    sortDirection: state.sortDirection,

    // Pagination state
    currentPage: state.currentPage,
    itemPerPage: state.itemPerPage,

    // Column configuration
    columnWidths,
    showSicColumn: !state.showFilterSidebar,

    // Computed data (the 3 memos)
    paginatedData,
    paginationMeta,

    // Event handlers
    handleTabChange,
    handleSearchSubmit,
    handleToggleSidebars,
    handleSort,
    setSearchInput,
    setCurrentPage,
    setItemPerPage,
    handleBookmarkClick,
    setSelectedChannel,
    setSelectedTimeOfDay,
    setSelectedDuration,
    setSelectedOwnership,
    resetFilters,
    setTimeFilter,
    handleWeekChange,
  };
};
